import { create } from "zustand";
import { devtools } from "zustand/middleware";
import {
  CommonNotificationType,
  INotificationBaseMessage,
  IPayloadUpdatedProductCategoryCode,
  ISupplierCompanyToProductCategory,
} from "@evennt-tech/api-v2-core";
import { apiV2 } from "api/firebase";
import { shallow } from "zustand/shallow";
import { registerStore } from "store/utils/reset-store.utils";

type State = {
  isLoading: boolean;
  codesBySupplier: Map<string, ISupplierCompanyToProductCategory[]>;
};

type Action = {
  autoCatch: () => () => void;
  fetchBySupplier: (supplierCompanyId: string) => Promise<void>;
  fetchMany: (
    supplierCompanyIds: string[],
    categoryIds: string[]
  ) => Promise<void>;
  reset: () => void;
};

type Store = State & Action;

export const categoryCodeStore = create(
  devtools<Store>(
    (set, get) => ({
      isLoading: false,
      codesBySupplier: new Map(),
      fetchBySupplier: async (supplierCompanyId) => {
        try {
          set({ isLoading: true });
          const supplierCategoryCodes = await apiV2.supplierCompany.getAllSupplierCompaniesToProductCategory(
            { supplierCompanyId }
          );
          const { codesBySupplier } = get();

          const newCodesBySupplier = new Map(codesBySupplier);

          newCodesBySupplier.set(supplierCompanyId, supplierCategoryCodes);

          set({ codesBySupplier: newCodesBySupplier });
        } catch (e) {
          console.error("Error occurred fetching category codes: ", e);
        } finally {
          set({ isLoading: false });
        }
      },
      fetchMany: async (supplierCompanyIds, categoryIds) => {
        try {
          set({ isLoading: true });

          const supplierCategoryCodes = await apiV2.supplierCompany.getManySupplierCompanyToProductCategory(
            { supplierCompanyIds, categoryIds }
          );
          const newCodesBySupplier = new Map(get().codesBySupplier);
          const touchedSuppliers = new Set<string>();

          supplierCategoryCodes.forEach((categoryCode) => {
            const { supplierCompanyId } = categoryCode;
            if (!touchedSuppliers.has(supplierCompanyId)) {
              touchedSuppliers.add(supplierCompanyId);
              newCodesBySupplier.set(supplierCompanyId, []);
            }
            newCodesBySupplier.get(supplierCompanyId)?.push(categoryCode);
          });

          supplierCompanyIds.forEach((supplierCompanyId) => {
            if (!newCodesBySupplier.has(supplierCompanyId)) {
              newCodesBySupplier.set(supplierCompanyId, []);
            }
          });

          set({ codesBySupplier: newCodesBySupplier });
        } catch (e) {
          console.error("Error occurred fetching category codes: ", e);
        } finally {
          set({ isLoading: false });
        }
      },
      autoCatch: () => {
        const { fetchBySupplier } = get();
        apiV2.notification.subscribe<
          INotificationBaseMessage<IPayloadUpdatedProductCategoryCode>
        >({
          key: CommonNotificationType.UPDATE_PRODUCT_CATEGORY_CODE,
          callback: async ({ payload }) => {
            if (!payload) return;

            const { codesBySupplier } = get();

            const shouldFetch = codesBySupplier.has(payload.supplierCompanyId);

            if (shouldFetch) {
              await fetchBySupplier(payload.supplierCompanyId);
            }
          },
        });

        return () =>
          apiV2.notification.unsubscribe({
            key: CommonNotificationType.UPDATE_PRODUCT_CATEGORY_CODE,
          });
      },
      reset: () => {
        set({ isLoading: false, codesBySupplier: new Map() });
      },
    }),
    { name: "category-code" }
  )
);

export const useCategoryCodeStore = <Return = Partial<Store>>(
  selector: (store: Store) => Return
) => categoryCodeStore(selector, shallow);

export const useCategoryCodeStoreActions = () =>
  useCategoryCodeStore(
    ({ autoCatch, fetchBySupplier, fetchMany, reset }) =>
      ({
        autoCatchCategoryCodes: autoCatch,
        fetchManyCategoryCodes: fetchMany,
        fetchCategoryCodesBySupplier: fetchBySupplier,
        resetCategoryCodes: reset,
      } as const)
  );

export const useSupplierCategoryCodes = (supplierCompanyId: string) =>
  useCategoryCodeStore<ISupplierCompanyToProductCategory[]>(
    ({ codesBySupplier }) => codesBySupplier.get(supplierCompanyId) ?? []
  );

export const useCategoryCodesState = () =>
  useCategoryCodeStore(
    ({ isLoading, codesBySupplier }) =>
      ({
        isCategoryCodesLoading: isLoading,
        categoryCodesBySupplier: codesBySupplier,
      } as const)
  );

registerStore(categoryCodeStore);
